  import React, { useState, useEffect, useCallback } from 'react';
import { FormikProvider, useFormik, Form } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { Button, TextField, Box, Stack, FormControlLabel, Checkbox, Grid, Typography, IconButton, Modal, Chip, Divider, Container, LinearProgress, InputAdornment, FormControl, Select, MenuItem, InputLabel, FormHelperText, Autocomplete } from '@mui/material';
import { Post, apiURL, Get, Put } from '../../../utils/apiCalls';
import { LoadingButton } from '@mui/lab';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LaunchIcon from '@mui/icons-material/Launch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PendingIcon from '@mui/icons-material/Pending';
import ErrorIcon from '@mui/icons-material/Error';
import CircularProgress from '@mui/material/CircularProgress';
import KYCService, { KYC_STATUS } from '../../../utils/kycService';
import { handleKYCError, handleKYCSuccess } from '../../../utils/kycUtils';
import { extractDocumentsFromArray } from '../../../utils/documentUtils';
import BusinessIcon from '@mui/icons-material/Business';
import useUserDetails from '../../../hooks/useUserDetails';
import OtpInput from 'react-otp-input';

// Styled Components
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});


// Validation Schema
const ProcurementSchema = Yup.object().shape({
  first_name: Yup.string().required('First Name is required'),
  last_name: Yup.string().required('Last Name is required'),
  email: Yup.string().email('Invalid Email').required('Email is required'),
  password: Yup.string().required('MPin is required').matches(/^[0-9]{4}$/, 'MPin must be exactly 4 digits'),
  phone: Yup.string().matches(/^[6-9]\d{9}$/, 'Please Enter valid Phone Number').required('Phone Number is required'),
  aadhar_number: Yup.string().matches(/^[2-9]{1}[0-9]{3}[0-9]{4}[0-9]{4}$/, 'Invalid Aadhar number').required('Aadhar Number is required'),
  pan_number: Yup.string().required('Pan Number is required'),
  address: Yup.string().required('Personal Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  country: Yup.string().required('Country is required'),
  pincode: Yup.string().required('Pincode is required'),
  bank_name: Yup.string().required('Bank Name is required'),
  account_number: Yup.string().required('Account Number is required'),
  bank_ifsc_code: Yup.string().required('IFSC Number is required'),
  bank_address: Yup.string().required('Bank Address is required'),
  active: Yup.string().required('Active is required'),
  role: Yup.string().required('Role is required'),
  ref_agent_id: Yup.string().nullable(),
  // KYC Fields
  pan_name: Yup.string(),
  pan_date_of_birth: Yup.date(),
  aadhaar_name: Yup.string(),
  bank_account_holder_name: Yup.string()
});

const ProcurementSchemaEdit = Yup.object().shape({
  first_name: Yup.string().required('First Name is required'),
  last_name: Yup.string().required('Last Name is required'),
  email: Yup.string().email('Invalid Email').required('Email is required'),
  phone: Yup.string().matches(/^[6-9]\d{9}$/, 'Please Enter valid Phone Number').required('Phone Number is required'),
  aadhar_number: Yup.string().matches(/^[2-9]{1}[0-9]{3}[0-9]{4}[0-9]{4}$/, 'Invalid Aadhar number').required('Aadhar Number is required'),
  pan_number: Yup.string().required('Pan Number is required'),
  address: Yup.string().required('Personal Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  country: Yup.string().required('Country is required'),
  pincode: Yup.string().required('Pincode is required'),
  bank_name: Yup.string().required('Bank Name is required'),
  account_number: Yup.string().required('Account Number is required'),
  bank_ifsc_code: Yup.string().required('IFSC Number is required'),
  bank_address: Yup.string().required('Bank Address is required'),
  active: Yup.string().required('Active is required'),
  role: Yup.string().required('Role is required'),
  ref_agent_id: Yup.string().nullable(),
  pan_name: Yup.string(),
  pan_date_of_birth: Yup.date(),
  aadhaar_name: Yup.string(),
  bank_account_holder_name: Yup.string()
});

const ProcurementEditCreate = () => {
  const { id, qr } = useParams();
  const userDetails = useUserDetails();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [upload, setUpload] = useState({
    aadhar_upload: '',
    pan_upload: ''
  });
  const [kycStatus, setKycStatus] = useState({
    pan: null,
    aadhaar: null,
    bank: null
  });
  const [kycLoading, setKycLoading] = useState({
    pan: false,
    aadhaar: false,
    bank: false
  });
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [verificationType, setVerificationType] = useState('');
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpType, setOtpType] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [procurementUsers, setProcurementUsers] = useState([]);

  const [data, setData] = useState({
    first_name: '',
    last_name: '',
    password: '',
    email: '',
    phone: '',
    aadhar_number: '',
    pan_number: '',
    aadhar_upload: {},
    pan_upload: {},
    address: '',
    bank_name: '',
    account_number: '',
    bank_ifsc_code: '',
    bank_address: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
    nominee_relation: '',
    nominee_age: '',
    nominee_name: '',
    active: 'yes',
    role: '',
    ref_agent_id: '',
    pan_name: '',
    pan_date_of_birth: '',
    aadhaar_name: '',
    bank_account_holder_name: '',
    pan_verified: false,
    aadhaar_verified: false,
    bank_verified: false,
    is_kyc:true
  });

  // Fetch procurement users (for parent/ref_agent_id selection)
  const fetchProcurementUsers = useCallback(async () => {
    try {
      const response = await Get(apiURL + '/api/v1/procurement-admin-users', '');
      if (response && response.data && response.data.status) {
        setProcurementUsers(Array.isArray(response.data.data) ? response.data.data : []);
      }
    } catch (error) {
      console.error('Error fetching procurement users:', error);
    }
  }, []);

  // Fetch procurement data for edit mode
  const fetchProcurementData = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const response = await Get(apiURL + `/api/v1/procurement-admin-users/${id}`,'');
      if (response && response.data && response.data.status) {
        const agentData = response.data.data;
        const bankDetails = agentData || {};
        
        // Extract documents
        const { panDocument, aadhaarDocument } = extractDocumentsFromArray(agentData.docs || []);
        
        setData({
          first_name: agentData.first_name || '',
          last_name: agentData.last_name || '',
          password: '',
          email: agentData.email || '',
          phone: agentData.phone || '',
          aadhar_number: agentData.aadhar_number || '',
          pan_number: agentData.pan_number || '',
          aadhar_upload: aadhaarDocument || {},
          pan_upload: panDocument || {},
          address: agentData.address || '',
          bank_name: bankDetails.bank_name || '',
          account_number: bankDetails.bank_account_number || '',
          bank_ifsc_code: bankDetails.bank_ifsc_code || '',
          bank_address: bankDetails.bank_address || '',
          city: agentData.city || '',
          state: agentData.state || '',
          country: agentData.country || '',
          pincode: agentData.pincode || '',
          nominee_relation: agentData.nominee_relation || '',
          nominee_age: agentData.nominee_age || '',
          nominee_name: agentData.nominee_name || '',
          active: agentData.active || 'yes',
          role: agentData.role?.level_code || agentData.role || '',
          ref_agent_id: agentData.ref_agent_id || '',
          pan_name: agentData.pan_name || '',
          pan_date_of_birth: agentData.pan_date_of_birth || '',
          aadhaar_name: agentData.aadhaar_name || '',
          bank_account_holder_name: bankDetails.name || '',
          pan_verified: agentData.pan_verified || false,
          aadhaar_verified: agentData.aadhaar_verified || false,
          bank_verified: agentData.bank_verified || false,
        });
        
        if (aadhaarDocument) {
          setUpload(prev => ({ ...prev, aadhar_upload: aadhaarDocument }));
        }
        if (panDocument) {
          setUpload(prev => ({ ...prev, pan_upload: panDocument }));
        }
        
        setIsEmailVerified(true); // Assume verified if editing
        setIsPhoneVerified(true);
      } else {
        toast.error('Failed to fetch procurement data');
        navigate('/dashboard/procurementUser');
      }
    } catch (error) {
      toast.error('Error fetching procurement data');
      navigate('/dashboard/procurementUser');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  // Fetch roles — read level_code synchronously from sessionStorage so roles
  // are fetched once on mount with the correct filter (level_code >= current user's level)
  const fetchRoles = useCallback(async () => {
    try {
      let levelCode;
      try {
        const storedUser = sessionStorage.getItem('user_details');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          levelCode = parsedUser?.role?.level_code;
        }
      } catch (e) {
        console.error('Error reading user_details from sessionStorage:', e);
      }

      const parsedLevelCode = parseInt(levelCode);
      const url = !isNaN(parsedLevelCode)
        ? `/api/v1/procurement-admin-roles?level_code=${parsedLevelCode}`
        : `/api/v1/procurement-admin-roles`;

      const response = await Get(apiURL + url, '');
      if (response && response.data && response.data.status) {
        setRoles(Array.isArray(response.data.data) ? response.data.data : []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  }, []); // stable — reads sessionStorage directly, no async hook dependency
  
  useEffect(() => {
    if (id) {
      fetchProcurementData();
    }
  }, [id, fetchProcurementData]);
  
  useEffect(() => {
    fetchRoles();
    fetchProcurementUsers();
  }, [fetchRoles, fetchProcurementUsers]);

  const formik = useFormik({
    initialValues: {
      ...data
    },
    enableReinitialize: true,
    validationSchema: id ? ProcurementSchemaEdit : ProcurementSchema,
    onSubmit: async (values, { setSubmitting, setFieldError, setFieldTouched }) => {
      // For create mode, check OTP and KYC verifications
      if (!id) {
        if (!isEmailVerified && !isPhoneVerified) {
          setFieldError('email', 'Please verify Email OTP');
          setFieldError('phone', 'Please verify Phone OTP');
          setFieldTouched('email', true);
          setFieldTouched('phone', true);
          setSubmitting(false);
          return;
        } else if (!isEmailVerified) {
          setFieldError('email', 'Please verify Email OTP');
          setFieldTouched('email', true);
          setSubmitting(false);
          return;
        } else if (!isPhoneVerified) {
          setFieldError('phone', 'Please verify Phone OTP');
          setFieldTouched('phone', true);
          setSubmitting(false);
          return;
        }
        
        if (!values.pan_verified || !values.aadhaar_verified || !values.bank_verified) {
          toast.error('Please complete all KYC verifications before submitting');
          setSubmitting(false);
          return;
        }
      }

      if (id) {
        // Edit mode - update existing procurement
        const updateValues = { ...values };
        if (!updateValues.password || updateValues.password.trim() === '') {
          delete updateValues.password;
        }
        const response = await Put(apiURL + '/api/v1/procurement-admin-users/', id, updateValues);
        if (response && response.data && response.data.status) {
          toast.success(response.data.message);
          setSubmitting(false);
          navigate('/dashboard/procurementUser');
        } else {
          toast.error(response?.data?.message || 'Please Try Again');
          setSubmitting(false);
        }
      } else {
        // Create mode
        const response = await Post(apiURL + '/api/v1/procurement-admin-users', { ...values, created_by: userDetails?.id, qr });
        if (response && response.data && response.data.status) {
          toast.success(response.data.message);
          setSubmitting(false);
          setSuccessModalOpen(true);
        } else {
          toast.error(response?.data.message || 'Please Try Again');
          setSubmitting(false);
        }
      }
    }
  });

  const { errors, touched, values, setFieldValue, setFieldError, isSubmitting, handleSubmit, getFieldProps, resetForm } = formik;

  // Handle document upload
  const handleDocUpload = async (fieldId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', fieldId === 'aadhar_upload' ? 'Aadhaar Card' : 'PAN Card');
    // If editing an existing user, pass their ID; otherwise backend will use authenticated admin's ID
    if (id) {
      formData.append('user_id', id);
    }
    
    const response = await Post(apiURL + '/api/v1/procurement-admin-docs/upload/procurement-admin-docs', formData, navigate);
    console.log( response.data ,response.data.status,"response");
    if (response && response.data && response.data.status) {
      setUpload((prev) => ({ ...prev, [fieldId]: response?.data?.data }));
      toast.success(`${fieldId === 'aadhar_upload' ? 'Aadhaar Card' : 'PAN Card'} Uploaded Successfully`);
      setFieldValue(fieldId, response?.data?.data?.id);
    } else {
      toast.error(response?.data?.message || 'Please Try Again');
    }
  };

  const handleFiles = async (e) => {
    const { id: fieldId, files } = e.target;
    if (!files || !files[0]) return;
    
    const fileSizeInMB = files[0]['size'] / (1024 * 1024);
    if (fileSizeInMB > 10) {
      toast.error('Please Upload File less than 10 MB');
      return;
    }
    
    handleDocUpload(fieldId, files[0]);
  };

  // OTP Verification Functions
  const handleOtpOpen = async (type) => {
    const response = await Post(apiURL + `/api/v1/users/registration/_sent_otp_registration`, { entity: type === 'email' ? values.email : values.phone });
    if (response && response.data && response.data.status) {
      toast.success('OTP sent successfully');
      setOtpType(type);
      setOtpModalOpen(true);
    } else {
      toast.error('Failed to send OTP. Please try again.');
    }
  };

  const handleOtpClose = () => {
    setOtpModalOpen(false);
    setOtpValue('');
  };

  const handleOtpSubmit = async () => {
    const response = await Post(apiURL + `/api/v1/users/registration/_verify_otp_registration`, { entity: otpType === 'email' ? values.email : values.phone, otp: otpValue });
    if (response && response.data && response.data.status) {
      toast.success('OTP Verified Successfully');
      setOtpModalOpen(false);
      setOtpValue('');
      if (otpType === 'email') {
        setIsEmailVerified(true);
        setFieldError('email', '');
      } else {
        setIsPhoneVerified(true);
        setFieldError('phone', '');
      }
    } else {
      toast.error('Invalid OTP. Please try again.');
    }
  };

  console.log(id,"id");
  // KYC Verification Functions
  const handlePANVerification = async () => {
    try {
      setKycLoading(prev => ({ ...prev, pan: true }));
      setVerificationType('PAN');
      setVerificationModalOpen(true);
      
      const panData = {
        pan_number: values.pan_number,
        date_of_birth: values.pan_date_of_birth,
        full_name: values.first_name + ' ' + values.last_name
      };

      const response = await KYCService.verifyPAN(panData, id || userDetails?.id);
      
      if (response?.data?.status) {
        handleKYCSuccess('PAN verification submitted successfully');
        setFieldValue('pan_verified', true);
        setKycStatus(prev => ({ ...prev, pan: KYC_STATUS.PROCESSING }));
      }
    } catch (error) {
      handleKYCError(error, 'PAN verification');
    } finally {
      setKycLoading(prev => ({ ...prev, pan: false }));
      setVerificationModalOpen(false);
    }
  };

  const handleAadhaarVerification = async () => {
    try {
      setKycLoading(prev => ({ ...prev, aadhaar: true }));
      setVerificationType('Aadhaar');
      setVerificationModalOpen(true);
      
      const aadhaarData = {
        aadhaar_number: values.aadhar_number,
        date_of_birth: values.pan_date_of_birth,
        // document_image: upload.aadhar_upload?.base64 || null
      };

      const response = await KYCService.verifyAadhaar(aadhaarData, id ||userDetails?.id);
      
      if (response?.data?.status) {
        handleKYCSuccess('Aadhaar verification submitted successfully');
        setFieldValue('aadhaar_verified', true);
        setKycStatus(prev => ({ ...prev, aadhaar: KYC_STATUS.PROCESSING }));
      }
    } catch (error) {
      handleKYCError(error, 'Aadhaar verification');
    } finally {
      setKycLoading(prev => ({ ...prev, aadhaar: false }));
      setVerificationModalOpen(false);
    }
  };

  const handleBankVerification = async () => {
    try {
      setKycLoading(prev => ({ ...prev, bank: true }));
      setVerificationType('Bank Account');
      setVerificationModalOpen(true);
      
      const bankData = {
        account_number: values.account_number,
        ifsc_code: values.bank_ifsc_code,
        account_holder_name: values.bank_account_holder_name
      };

      const response = await KYCService.verifyBank(bankData, userDetails?.id);
      
      if (response?.data?.status) {
        handleKYCSuccess('Bank verification submitted successfully');
        setFieldValue('bank_verified', true);
        setKycStatus(prev => ({ ...prev, bank: KYC_STATUS.PROCESSING }));
      }
    } catch (error) {
      handleKYCError(error, 'Bank verification');
    } finally {
      setKycLoading(prev => ({ ...prev, bank: false }));
      setVerificationModalOpen(false);
    }
  };

  const handleChangeCheck = (e) => {
    setChecked(e.target.checked);
  };

  const handleSuccessClose = () => {
    setSuccessModalOpen(false);
    resetForm();
    setChecked(false);
    setUpload({ aadhar_upload: '', pan_upload: '' });
    setKycStatus({ pan: null, aadhaar: null, bank: null });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case KYC_STATUS.VERIFIED:
        return <VerifiedUserIcon color="success" />;
      case KYC_STATUS.PROCESSING:
        return <PendingIcon color="warning" />;
      case KYC_STATUS.REJECTED:
        return <ErrorIcon color="error" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 0}}>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Box sx={{ maxWidth: 1200, margin: '0 auto' }}>
            
              {/* Header */}
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Box
                  sx={{
                    bgcolor: "#1976d2",
                    borderRadius: "50%",
                    width: 64,
                    height: 64,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BusinessIcon sx={{ color: "#fff", fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="text.primary">
                    {id ? 'Edit Procurement Registration' : 'Procurement Registration'}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    {id ? 'Update procurement agent details' : 'Complete KYC verification to become a procurement'}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ mb: 3 }} />

              {/* Progress Indicator - Only for create mode */}
              {!id && (
                <Box sx={{ mb: 4 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={((values.pan_verified ? 1 : 0) + (values.aadhaar_verified ? 1 : 0) + (values.bank_verified ? 1 : 0)) * 33.33}
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: '#e2e8f0',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 4,
                      }
                    }} 
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    KYC Progress: {((values.pan_verified ? 1 : 0) + (values.aadhaar_verified ? 1 : 0) + (values.bank_verified ? 1 : 0))}/3 completed
                  </Typography>
                </Box>
              )}

              {/* Section 1: Personal Information */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                  1. Personal Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      {...getFieldProps("first_name")}
                      error={Boolean(touched.first_name && errors.first_name)}
                      helperText={touched.first_name && errors.first_name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      {...getFieldProps("last_name")}
                      error={Boolean(touched.last_name && errors.last_name)}
                      helperText={touched.last_name && errors.last_name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      {...getFieldProps("email")}
                      error={Boolean(touched.email && errors.email)}
                      helperText={touched.email && errors.email}
                      disabled={!!id}
                      InputProps={{
                        endAdornment: !id && (
                          <InputAdornment position="end">
                            {isEmailVerified ? (
                              <CheckCircleIcon color="success" />
                            ) : (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleOtpOpen("email")}
                              >
                                Verify
                              </Button>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        MPin
                      </Typography>
                      <OtpInput
                        numInputs={4}
                        value={values.password}
                        onChange={(val) => setFieldValue('password', val)}
                        isDisabled={!!id}
                        containerStyle={{ gap: '15px', display: 'flex' }}
                        inputStyle={{
                          color: '#001B71',
                          WebkitTextFillColor: 'black !important',
                          padding: '0px',
                          fontWeight: 600,
                          fontSize: '1.25rem',
                          borderRadius: '4px',
                          boxSizing: 'content-box',
                          height: '50px',
                          display: 'block',
                          width: '45px',
                          minWidth: '0px',
                          border: '1px solid #c4c4c4',
                          opacity: id ? 0.6 : 1
                        }}
                        renderInput={(props) => <input {...props} type="password" />}
                      />
                      {touched.password && errors.password && (
                        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                          {errors.password}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {id ? 'Leave blank to keep current password' : 'Enter 4 digit MPin characters'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      {...getFieldProps("phone")}
                      error={Boolean(touched.phone && errors.phone)}
                      helperText={touched.phone && errors.phone}
                      disabled={!!id}
                      InputProps={{
                        endAdornment: !id && (
                          <InputAdornment position="end">
                            {isPhoneVerified ? (
                              <CheckCircleIcon color="success" />
                            ) : (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleOtpOpen("phone")}
                              >
                                Verify
                              </Button>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Section 2: PAN Verification */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                  2. PAN Card Verification
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="PAN Number"
                      {...getFieldProps("pan_number")}
                      error={Boolean(touched.pan_number && errors.pan_number)}
                      helperText={touched.pan_number && errors.pan_number}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      disabled={values.pan_verified && !!id}
                      InputProps={{
                        endAdornment: values.pan_verified ? (
                          <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                        ) : null
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Date of Birth"
                      InputLabelProps={{ shrink: true }}
                      {...getFieldProps("pan_date_of_birth")}
                      error={Boolean(touched.pan_date_of_birth && errors.pan_date_of_birth)}
                      helperText={touched.pan_date_of_birth && errors.pan_date_of_birth}
                      disabled={values.pan_verified && !!id}
                      InputProps={{
                        endAdornment: values.pan_verified ? (
                          <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                        ) : null
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={2}>
                      <Button
                        component="label"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        disabled={values.pan_verified && !!id}
                      >
                        Upload PAN Card
                        <VisuallyHiddenInput
                          id="pan_upload"
                          type="file"
                          onChange={(e) => handleFiles(e)}
                        />
                      </Button>
                      {console.log(upload,"upload.pan_upload")}
                      <Stack direction={"row"} spacing={1}>
                        
                        <Typography sx={{ display: "flex", alignItems: "center" }}>
                          {upload.pan_upload?.["s3_url"]
                            ? upload.pan_upload?.["s3_url"]
                            : "No File Chosen"}
                        </Typography>
                        {upload.pan_upload?.["s3_url"] ? (
                          <IconButton
                            component={"a"}
                            href={ upload.pan_upload?.["s3_url"] }
                            target="_blank"
                            rel="noreferrer"
                          >
                            <LaunchIcon />
                          </IconButton>
                        ) : null}
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
                
                {!id && (
                  <Box sx={{ mt: 3 }}>
                    <LoadingButton
                      variant="contained"
                      onClick={handlePANVerification}
                      loading={kycLoading.pan}
                      disabled={!values.pan_number || !values.pan_date_of_birth || !upload.pan_upload?.s3_url || values.pan_verified}
                      startIcon={getStatusIcon(kycStatus.pan)}
                      sx={{ mr: 2 }}
                    >
                      {kycLoading.pan ? 'Verifying...' : values.pan_verified ? 'Verified' : 'Verify PAN'}
                    </LoadingButton>
                  </Box>
                )}
              </Box>

              {/* Section 3: Aadhaar Verification */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                  3. Aadhaar Card Verification
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Aadhaar Number"
                      {...getFieldProps("aadhar_number")}
                      error={Boolean(touched.aadhar_number && errors.aadhar_number)}
                      helperText={touched.aadhar_number && errors.aadhar_number}
                      disabled={values.aadhaar_verified && !!id}
                      InputProps={{
                        endAdornment: values.aadhaar_verified ? (
                          <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                        ) : null
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack spacing={2}>
                      <Button
                        component="label"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        disabled={values.aadhaar_verified && !!id}
                      >
                        Upload Aadhaar Card
                        <VisuallyHiddenInput
                          id="aadhar_upload"
                          type="file"
                          onChange={(e) => handleFiles(e)}
                        />
                      </Button>
                      
                      <Stack direction={"row"} spacing={1}>
                        <Typography sx={{ display: "flex", alignItems: "center" }}>
                          {upload.aadhar_upload?.["s3_url"]
                            ? upload.aadhar_upload?.["s3_url"]
                            : "No File Chosen"}
                        </Typography>
                        {upload.aadhar_upload?.["s3_url"] ? (
                          <IconButton
                            component={"a"}
                            href={ upload.aadhar_upload?.["s3_url"] }
                            target="_blank"
                            rel="noreferrer"
                          >
                            <LaunchIcon />
                          </IconButton>
                        ) : null}
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>

                {!id && (
                  <Box sx={{ mt: 3 }}>
                    <LoadingButton
                      variant="contained"
                      onClick={handleAadhaarVerification}
                      loading={kycLoading.aadhaar}
                      disabled={!values.aadhar_number || !upload.aadhar_upload?.["s3_url"] || values.aadhaar_verified}
                      startIcon={getStatusIcon(kycStatus.aadhaar)}
                      sx={{ mr: 2 }}
                    >
                      {kycLoading.aadhaar ? 'Verifying...' : values.aadhaar_verified ? 'Verified' : 'Verify Aadhaar'}
                    </LoadingButton>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Section 4: Bank Verification */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                  4. Bank Account Verification
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Bank Name"
                      {...getFieldProps("bank_name")}
                      error={Boolean(touched.bank_name && errors.bank_name)}
                      helperText={touched.bank_name && errors.bank_name}
                      disabled={values.bank_verified && !!id}
                      InputProps={{
                        endAdornment: values.bank_verified ? (
                          <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                        ) : null
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Account Number"
                      {...getFieldProps("account_number")}
                      error={Boolean(touched.account_number && errors.account_number)}
                      helperText={touched.account_number && errors.account_number}
                      disabled={values.bank_verified && !!id}
                      InputProps={{
                        endAdornment: values.bank_verified ? (
                          <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                        ) : null
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="IFSC Code"
                      {...getFieldProps("bank_ifsc_code")}
                      error={Boolean(touched.bank_ifsc_code && errors.bank_ifsc_code)}
                      helperText={touched.bank_ifsc_code && errors.bank_ifsc_code}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      disabled={values.bank_verified && !!id}
                      InputProps={{
                        endAdornment: values.bank_verified ? (
                          <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                        ) : null
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Account Holder Name"
                      {...getFieldProps("bank_account_holder_name")}
                      error={Boolean(touched.bank_account_holder_name && errors.bank_account_holder_name)}
                      helperText={touched.bank_account_holder_name && errors.bank_account_holder_name}
                      disabled={values.bank_verified && !!id}
                      InputProps={{
                        endAdornment: values.bank_verified ? (
                          <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                        ) : null
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Bank Address"
                      {...getFieldProps("bank_address")}
                      error={Boolean(touched.bank_address && errors.bank_address)}
                      helperText={touched.bank_address && errors.bank_address}
                      disabled={values.bank_verified && !!id}
                      InputProps={{
                        endAdornment: values.bank_verified ? (
                          <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
                        ) : null
                      }}
                    />
                  </Grid>
                </Grid>

                {!id && (
                  <Box sx={{ mt: 3 }}>
                    <LoadingButton
                      variant="contained"
                      onClick={handleBankVerification}
                      loading={kycLoading.bank}
                      disabled={!values.bank_name || !values.account_number || !values.bank_ifsc_code || !values.bank_account_holder_name || values.bank_verified}
                      startIcon={getStatusIcon(kycStatus.bank)}
                      sx={{ mr: 2 }}
                    >
                      {kycLoading.bank ? 'Verifying...' : values.bank_verified ? 'Verified' : 'Verify Bank'}
                    </LoadingButton>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Section 5: Additional Details */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, color: 'primary.main' }}>
                  5. Additional Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Personal Address"
                      {...getFieldProps("address")}
                      error={Boolean(touched.address && errors.address)}
                      helperText={touched.address && errors.address}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      {...getFieldProps("city")}
                      error={Boolean(touched.city && errors.city)}
                      helperText={touched.city && errors.city}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="State"
                      {...getFieldProps("state")}
                      error={Boolean(touched.state && errors.state)}
                      helperText={touched.state && errors.state}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Country"
                      {...getFieldProps("country")}
                      error={Boolean(touched.country && errors.country)}
                      helperText={touched.country && errors.country}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Pincode"
                      {...getFieldProps("pincode")}
                      error={Boolean(touched.pincode && errors.pincode)}
                      helperText={touched.pincode && errors.pincode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Nominee Name"
                      {...getFieldProps("nominee_name")}
                      error={Boolean(touched.nominee_name && errors.nominee_name)}
                      helperText={touched.nominee_name && errors.nominee_name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      type="number"
                      fullWidth
                      label="Nominee Age"
                      InputProps={{ inputProps: { min: 0, max: 100 } }}
                      {...getFieldProps("nominee_age")}
                      error={Boolean(touched.nominee_age && errors.nominee_age)}
                      helperText={touched.nominee_age && errors.nominee_age}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Nominee Relation"
                      {...getFieldProps("nominee_relation")}
                      error={Boolean(touched.nominee_relation && errors.nominee_relation)}
                      helperText={touched.nominee_relation && errors.nominee_relation}
                    />
                  </Grid>
                  {id && (
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        fullWidth
                        name="ref_agent_id"
                        id="ref_agent_id"
                        value={values.ref_agent_id && Array.isArray(procurementUsers) ? procurementUsers.find((data) => data.id === values.ref_agent_id) || null : null}
                        options={Array.isArray(procurementUsers) ? procurementUsers.filter((u) => u.id !== parseInt(id)) : []}
                        getOptionLabel={(option) => option ? `${option.first_name} ${option.last_name}` : ''}
                        renderOption={(props, option) => (
                          <Box component="li" sx={{ minWidth: '100px' }} {...props}>
                            {option.first_name}&nbsp;{option.last_name}
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Procurement Partner (Associate)"
                            error={Boolean(touched.ref_agent_id && errors.ref_agent_id)}
                            helperText={touched.ref_agent_id && errors.ref_agent_id}
                          />
                        )}
                        onChange={(e, newValue) => {
                          setFieldValue('ref_agent_id', newValue ? newValue.id : '');
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      fullWidth
                      name="role"
                      id="role"
                      value={values.role && Array.isArray(roles) ? roles.find((data) => String(data.level_code) === String(values.role)) || null : null}
                      options={Array.isArray(roles) ? roles : []}
                      getOptionLabel={(option) => option.designation ?? option ?? ''}
                      renderOption={(props, option) => (
                        <Box component="li" sx={{ minWidth: '100px' }} {...props}>
                          {option.designation}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Position or Role"
                          error={Boolean(touched.role && errors.role)}
                          helperText={touched.role && errors.role}
                        />
                      )}
                      onChange={(e, newValue, reason) => {
                        setFieldValue('role', newValue ? newValue['level_code'] : '');
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={Boolean(touched.active && errors.active)}>
                      <InputLabel id="active">Active</InputLabel>
                      <Select
                        id="active"
                        value={values.active}
                        label="Active"
                        onChange={(e) => setFieldValue('active', e.target.value)}
                      >
                        <MenuItem value={'yes'}>Yes</MenuItem>
                        <MenuItem value={'no'}>No</MenuItem>
                      </Select>
                      <FormHelperText>{touched.active && errors.active}</FormHelperText>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* KYC Status Summary - Only for create mode */}
                {!id && (
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      KYC Verification Status
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Chip
                        label={`PAN: ${values.pan_verified ? 'VERIFIED' : 'PENDING'}`}
                        color={values.pan_verified ? 'success' : 'warning'}
                        icon={getStatusIcon(values.pan_verified ? KYC_STATUS.VERIFIED : KYC_STATUS.PENDING)}
                      />
                      <Chip
                        label={`Aadhaar: ${values.aadhaar_verified ? 'VERIFIED' : 'PENDING'}`}
                        color={values.aadhaar_verified ? 'success' : 'warning'}
                        icon={getStatusIcon(values.aadhaar_verified ? KYC_STATUS.VERIFIED : KYC_STATUS.PENDING)}
                      />
                      <Chip
                        label={`Bank: ${values.bank_verified ? 'VERIFIED' : 'PENDING'}`}
                        color={values.bank_verified ? 'success' : 'warning'}
                        icon={getStatusIcon(values.bank_verified ? KYC_STATUS.VERIFIED : KYC_STATUS.PENDING)}
                      />
                    </Stack>
                  </Box>
                )}
              </Box>

              {/* Terms and Conditions */}
              <Stack ml={"20px"} spacing={1} direction={"row"} sx={{ mt: 4 }}>
                <FormControlLabel
                  value="end"
                  control={
                    <Checkbox checked={checked} onChange={handleChangeCheck} />
                  }
                  label=""
                  labelPlacement="end"
                />
                <Typography
                  align="center"
                  justifyContent={"center"}
                  display={"flex"}
                  alignItems={"center"}
                >
                  I Accept the{" "}
                  <a
                    href={require("../../../img/terms and conditions.pdf")}
                    target="_blank"
                    rel="noreferrer"
                    style={{ cursor: "pointer", marginLeft: "5px" }}
                  >
                    Terms and Conditions
                  </a>
                </Typography>
              </Stack>
          

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button
                onClick={() => navigate('/dashboard/procurement')}
                variant="contained"
                sx={{
                  backgroundColor: "#3f51b5",
                  "&:hover": { color: "#ffff", backgroundColor: "#3f51b5" },
                }}
              >
                Back
              </Button>
              
              <LoadingButton
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
                disabled={!checked || (!id && (!values.pan_verified || !values.aadhaar_verified || !values.bank_verified))}
                sx={{
                  backgroundColor: "#3f51b5",
                  "&:hover": { color: "#ffff", backgroundColor: "#3f51b5" },
                }}
              >
                {id ? 'Update' : 'Submit'}
              </LoadingButton>
            </Box>
          </Box>

          {/* Verification Modal */}
          <Modal open={verificationModalOpen}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 400,
                backgroundColor: 'white',
                borderRadius: 2,
                boxShadow: 24,
                p: 4,
                textAlign: 'center'
              }}
            >
              <CircularProgress 
                size={64} 
                sx={{ 
                  color: 'primary.main', 
                  mb: 2 
                }} 
              />
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Verifying {verificationType}...
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                Please wait while we verify your {verificationType.toLowerCase()} information. This may take a few moments.
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                gap: 1,
                mt: 2
              }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Processing...
                </Typography>
              </Box>
            </Box>
          </Modal>

          {/* Success Modal - Only for create mode */}
          {!id && (
            <Modal open={successModalOpen} onClose={handleSuccessClose}>
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 400,
                  backgroundColor: 'white',
                  borderRadius: 2,
                  boxShadow: 24,
                  p: 4,
                  textAlign: 'center'
                }}
              >
                <CheckCircleIcon 
                  sx={{ 
                    fontSize: 64, 
                    color: 'success.main', 
                    mb: 2 
                  }} 
                />
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                  Procurement Agent Created Successfully!
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                  Your procurement agent application has been submitted and is under review.
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button
                    variant="outlined"
                    onClick={handleSuccessClose}
                    sx={{ minWidth: 100 }}
                  >
                    Create Another
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleSuccessClose();
                      navigate('/dashboard/procurementUser');
                    }}
                    sx={{ minWidth: 100 }}
                  >
                    Go to Dashboard
                  </Button>
                </Stack>
              </Box>
            </Modal>
          )}

          {/* OTP Verification Modal */}
          <Modal open={otpModalOpen} onClose={handleOtpClose}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 400,
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 24,
                p: 4,
              }}
            >
              <Typography variant="h6" align="center" gutterBottom>
                Enter OTP for {otpType === "email" ? "Email" : "Phone"}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 3, width: '100%' }}>
                <OtpInput
                  numInputs={4}
                  value={otpValue}
                  onChange={(val) => setOtpValue(val)}
                  containerStyle={{ gap: '20px', display: 'flex', justifyContent: 'center' }}
                  inputStyle={{
                    color: '#001B71',
                    WebkitTextFillColor: 'black !important',
                    padding: '0px',
                    fontWeight: 600,
                    fontSize: '1.25rem',
                    borderRadius: '4px',
                    boxSizing: 'content-box',
                    height: '50px',
                    display: 'block',
                    width: '45px',
                    minWidth: '0px',
                    border: '2px solid #BDCEE9'
                  }}
                  renderInput={(props) => <input {...props} type="text" />}
                />
                {!otpValue && (
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    OTP is required
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={handleOtpClose}
                  sx={{ minWidth: 100 }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleOtpSubmit}
                  disabled={!otpValue}
                  sx={{ minWidth: 100 }}
                >
                  Verify OTP
                </Button>
              </Stack>
            </Box>
          </Modal>
        </Form>
      </FormikProvider>
    </Container>
  );
};

export default ProcurementEditCreate;

